import { AffineTransform3D, AxisAlignedBoundingBox, Orientation3D, OrientedBoundingBox, Position3D, Vector3D, } from '@fixtuur/fine';
import * as Either from 'fp-ts/Either';
export * as Rules from './modularSofa/rules';
export function getPropertyOptions(modularTemplate) {
    return Object.values(modularTemplate.modules)
        .flat()
        .map(moduleOption => moduleOption.properties)
        .reduce((allPropertyOptions, moduleProperties, i) => {
        Object.entries(moduleProperties).forEach(([property, value]) => {
            const options = (allPropertyOptions[property] =
                allPropertyOptions[property] ?? (i > 0 ? [null] : []));
            if (!options.includes(value)) {
                options.push(value);
            }
        });
        return allPropertyOptions;
    }, {});
}
export const getFabricTypes = (configurator) => configurator.globalConfigurableProperties['fabric']?.groups;
export function countEqualProperties(a, b) {
    return Object.entries(a).filter(([k, aValue]) => aValue === b[k]).length;
}
export function getAvailableModulesForGlobalSelections(modularTemplate, globalSelections) {
    return Object.fromEntries(Object.entries(modularTemplate.modules).map(([moduleType, modules]) => [
        moduleType,
        modules.filter(furnitureModule => Object.entries(globalSelections).every(([property, value]) => !(property in furnitureModule.properties) ||
            value === furnitureModule.properties[property])),
    ]));
}
// TODO: handle missing module option
export function getModuleById(modularTemplate, piece) {
    return Object.entries(modularTemplate.modules).flatMap(([kind, moduleOptions]) => {
        return moduleOptions
            .filter(option => option.particleId === piece)
            .map((option) => [
            // Cast back to ModuleType is safe as Object.entries loses the detailed type of the key
            kind,
            option,
        ]);
    })[0];
}
export function updateGlobalPropertySelection(currentPieces, modularTemplate, globalSelections) {
    const availableModules = getAvailableModulesForGlobalSelections(modularTemplate, globalSelections);
    return currentPieces.map(currentPiece => {
        const [moduleType, moduleOption] = getModuleById(modularTemplate, currentPiece);
        const currentProperties = moduleOption.properties;
        const replacementCandidates = (availableModules[moduleType] ?? []).slice().sort((a, b) => {
            return (countEqualProperties(currentProperties, b.properties) -
                countEqualProperties(currentProperties, a.properties));
        });
        return replacementCandidates[0].particleId;
    });
}
function placeCornerPiece(pieceBounds, rightHanded, currentPosition, currentOrientation) {
    const nextOrientation = Orientation3D.fromAxisAngle(Vector3D.unitY(), -Math.PI / 2).compose(currentOrientation);
    const placementOrientation = rightHanded ? currentOrientation : nextOrientation;
    const moduleOriginFromLeftPoint = new Vector3D(rightHanded
        ? [-pieceBounds.min().x(), -pieceBounds.min().y(), -pieceBounds.min().z()]
        : [-pieceBounds.min().x(), -pieceBounds.min().y(), -pieceBounds.max().z()]);
    const moduleRightPointFromLeftPoint = new Vector3D(rightHanded
        ? [
            pieceBounds.max().x() - pieceBounds.min().x(),
            0,
            pieceBounds.max().z() - pieceBounds.min().z(),
        ]
        : [
            pieceBounds.max().x() - pieceBounds.min().x(),
            0,
            pieceBounds.min().z() - pieceBounds.max().z(),
        ]);
    const leftActionFromOrigin = new Vector3D(rightHanded
        ? [
            pieceBounds.min().x(),
            (pieceBounds.min().y() + pieceBounds.max().y()) / 2,
            (pieceBounds.min().z() + pieceBounds.max().z()) / 2,
        ]
        : [
            (pieceBounds.min().x() + pieceBounds.max().x()) / 2,
            (pieceBounds.min().y() + pieceBounds.max().y()) / 2,
            pieceBounds.max().z(),
        ]);
    const rightActionFromOrigin = new Vector3D(rightHanded
        ? [
            (pieceBounds.min().x() + pieceBounds.max().x()) / 2,
            (pieceBounds.min().y() + pieceBounds.max().y()) / 2,
            pieceBounds.max().z(),
        ]
        : [
            pieceBounds.max().x(),
            (pieceBounds.min().y() + pieceBounds.max().y()) / 2,
            (pieceBounds.min().z() + pieceBounds.max().z()) / 2,
        ]);
    const topActionFromOrigin = new Vector3D([
        (pieceBounds.min().x() + pieceBounds.max().x()) / 2,
        pieceBounds.max().y() * 1.25,
        (pieceBounds.min().z() + pieceBounds.max().z()) / 2,
    ]);
    const placementPosition = currentPosition.translate(placementOrientation.rotateVector(moduleOriginFromLeftPoint));
    return {
        placementPosition,
        placementOrientation: placementOrientation,
        currentPosition: currentPosition.translate(placementOrientation.rotateVector(moduleRightPointFromLeftPoint)),
        currentOrientation: nextOrientation,
        leftActionPosition: placementPosition.translate(placementOrientation.rotateVector(leftActionFromOrigin)),
        rightActionPosition: placementPosition.translate(placementOrientation.rotateVector(rightActionFromOrigin)),
        topActionPosition: placementPosition.translate(placementOrientation.rotateVector(topActionFromOrigin)),
    };
}
function placeStraightPiece(pieceBounds, _rightHanded, currentPosition, currentOrientation) {
    const moduleOriginFromLeftPoint = new Vector3D([
        -pieceBounds.min().x(),
        -pieceBounds.min().y(),
        -pieceBounds.min().z(),
    ]);
    const moduleRightPointFromLeftPoint = new Vector3D([
        pieceBounds.max().x() - pieceBounds.min().x(),
        0,
        0,
    ]);
    const leftActionFromOrigin = new Vector3D([
        pieceBounds.min().x(),
        (pieceBounds.min().y() + pieceBounds.max().y()) / 2,
        (pieceBounds.min().z() + pieceBounds.max().z()) / 2,
    ]);
    const rightActionFromOrigin = new Vector3D([
        pieceBounds.max().x(),
        (pieceBounds.min().y() + pieceBounds.max().y()) / 2,
        (pieceBounds.min().z() + pieceBounds.max().z()) / 2,
    ]);
    const topActionFromOrigin = new Vector3D([
        (pieceBounds.min().x() + pieceBounds.max().x()) / 2,
        pieceBounds.max().y() * 1.25,
        (pieceBounds.min().z() + pieceBounds.max().z()) / 2,
    ]);
    const placementPosition = currentPosition.translate(currentOrientation.rotateVector(moduleOriginFromLeftPoint));
    return {
        placementPosition,
        placementOrientation: currentOrientation,
        currentPosition: currentPosition.translate(currentOrientation.rotateVector(moduleRightPointFromLeftPoint)),
        currentOrientation,
        leftActionPosition: placementPosition.translate(currentOrientation.rotateVector(leftActionFromOrigin)),
        rightActionPosition: placementPosition.translate(currentOrientation.rotateVector(rightActionFromOrigin)),
        topActionPosition: placementPosition.translate(currentOrientation.rotateVector(topActionFromOrigin)),
    };
}
/**
 * Correct sofa pieces' positions and orientations to make the supplied position and orientation
 * the new default pose
 */
function repositionSofa(position, rotation, placed) {
    const rotationFix = rotation.inverse();
    const correctPosition = (originalPosition) => {
        const difference = originalPosition.subtract(position);
        const corrected = rotationFix.rotateVector(difference);
        return new Position3D(0, 0, 0).translate(corrected);
    };
    for (const placedPiece of placed) {
        placedPiece.orientation = rotationFix.compose(placedPiece.orientation);
        placedPiece.position = correctPosition(placedPiece.position);
        placedPiece.leftActionPosition = correctPosition(placedPiece.leftActionPosition);
        placedPiece.rightActionPosition = correctPosition(placedPiece.rightActionPosition);
        placedPiece.topActionPosition = correctPosition(placedPiece.topActionPosition);
    }
}
export function placePieceSequence(pieces, modularTemplate, useProductDimensions = false) {
    let currentPosition = new Position3D(0, 0, 0);
    let currentOrientation = Orientation3D.identity();
    const placed = [];
    for (let i = 0; i < pieces.length; i++) {
        const piece = pieces[i];
        let placementPosition, placementOrientation, leftActionPosition, rightActionPosition, topActionPosition;
        const [moduleType, moduleOption] = getModuleById(modularTemplate, piece);
        const placePiece = moduleType === 'connecting-corner' ? placeCornerPiece : placeStraightPiece;
        const bounds = useProductDimensions
            ? {
                min: new Position3D(moduleOption.productDims.width
                    ? -moduleOption.productDims.width / 2
                    : moduleOption.bounds.min.x, moduleOption.productDims.height ? 0 : moduleOption.bounds.min.y, moduleOption.productDims.depth
                    ? -moduleOption.productDims.depth / 2
                    : moduleOption.bounds.min.z),
                max: new Position3D(moduleOption.productDims.width
                    ? moduleOption.productDims.width / 2
                    : moduleOption.bounds.max.x, moduleOption.productDims.height
                    ? moduleOption.productDims.height
                    : moduleOption.bounds.max.y, moduleOption.productDims.depth
                    ? moduleOption.productDims.depth / 2
                    : moduleOption.bounds.max.z),
            }
            : {
                min: new Position3D(moduleOption.bounds.min.x, moduleOption.bounds.min.y, moduleOption.bounds.min.z),
                max: new Position3D(moduleOption.bounds.max.x, moduleOption.bounds.max.y, moduleOption.bounds.max.z),
            };
        ({
            placementPosition,
            placementOrientation,
            currentOrientation,
            currentPosition,
            leftActionPosition,
            rightActionPosition,
            topActionPosition,
        } = placePiece(new AxisAlignedBoundingBox(bounds.min, bounds.max), moduleOption.bounds.rightHanded, currentPosition, currentOrientation));
        placed.push({
            particleId: piece,
            position: placementPosition,
            orientation: placementOrientation,
            leftActionPosition,
            rightActionPosition,
            topActionPosition,
            bounds,
        });
    }
    // TODO: Do we need a richer notion of the role a piece plays in a sofa?
    // e.g if we're treating corner pieces as ends for relaxed ruleset?
    const firstCorner = placed.find(piece => getModuleById(modularTemplate, piece.particleId)[0] === 'connecting-corner');
    if (firstCorner) {
        repositionSofa(firstCorner.position, firstCorner.orientation, placed);
    }
    return placed;
}
export function calculatePlacedDimensions(placedPieces) {
    const bounds = placedPieces.reduce((bounds, canonicalPiece) => bounds.union(Either.getOrElse(() => new AxisAlignedBoundingBox(new Position3D(0, 0, 0), new Position3D(0, 0, 0)))(new OrientedBoundingBox(new AxisAlignedBoundingBox(canonicalPiece.bounds.min, canonicalPiece.bounds.max), new AffineTransform3D(canonicalPiece.orientation.matrix(), canonicalPiece.position.subtract(new Position3D(0, 0, 0)))).axisAlignedBoundingBox())), new AxisAlignedBoundingBox(new Position3D(0, 0, 0), new Position3D(0, 0, 0)));
    return bounds.max().subtract(bounds.min()).value();
}
